import React, { useState, useRef, useCallback, useEffect } from 'react';
import ChatBulkAuditTaskFeedbackBar from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditTaskFeedbackBar';
import ChatBulkAuditContributorFeedbackBar from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditContributorFeedbackBar';
import ChatBulkAuditMultiTurnResponse from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditMultiTurnResponse';
import {
  ChatBulkAuditBatch,
  SubtaskAssignment,
  SubtaskAssignmentAbbreviatedResponse,
} from '../SpeedAudit/types';
import { IUserJson } from '@scale/shared/types/user';
import { AuditRubric } from '@scale/shared-types';
import { Tabs } from '@scale/scaleui';
import _ from 'lodash';
import { feedbackBarWidthPixels } from './layoutHelpers';
import { useChatLayoutStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatLayoutStore';
import { usePinStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/usePinStore';
import { QAOperationHeader } from 'react-scale/components/remoAdmin/qaOperation/QAOperationSubmitModal';
import { useQAOperationContext } from 'react-scale/components/remoAdmin/qaOperation/qaOperationContext';

interface ChatBulkAuditPageProps {
  auditBatch: ChatBulkAuditBatch;
  rubric?: AuditRubric;
  currentAttemptAudit: number;
  subtaskAssignment?: SubtaskAssignment;
  setSelectedAnnotation: (index: number) => void;
  subtaskAssignmentsLocalCopy: SubtaskAssignmentAbbreviatedResponse[];
  user: IUserJson;
  userHasProjectRole: boolean;
  userHasClaimTaskPerms: boolean;
  allowEdits: boolean;
  formattedResponseView: boolean;
  auditorHasClaim: boolean;
  showFeedbackBar: boolean;
  setShowFeedbackBar: (showFeedbackBar: boolean) => void;
}

interface AutosavingFeedback {
  autosaveFeedback(): Promise<void>;
}

enum TaskFeedbackTabLabel {
  TaskFeedback = 'Task Feedback',
  ContributorFeedback = 'Contributor Notes',
}
const tabs = _.values(TaskFeedbackTabLabel).map(label => ({ id: label, label }));

function ChatBulkAuditPage(props: ChatBulkAuditPageProps) {
  const [selectedAttempt, setSelectedAttempt] = useState<number>(0);
  const [feedbackType, setFeedbackType] = useState<TaskFeedbackTabLabel>(
    TaskFeedbackTabLabel.TaskFeedback,
  );
  const [isOperationModalOpen, setIsOperationModalOpen] = useState(false);

  const resetHasDrawerBeenOpened = useChatLayoutStore(state => state.resetHasDrawerBeenOpened);
  const resetPinStore = usePinStore(state => state.reset);

  const taskBarRef = useRef<AutosavingFeedback>(null);
  const contributorBarRef = useRef<AutosavingFeedback>(null);

  const updateAttemptAudit = useCallback(
    (index: number) => {
      if (index !== props.currentAttemptAudit) {
        setSelectedAttempt(0);
      }
      props.setSelectedAnnotation(index);
      if (index !== props.currentAttemptAudit) {
        // close drawer and reset pin store
        // we have to put it here because somehow the drawer will just add another one on the parent setState call
        resetHasDrawerBeenOpened();
        resetPinStore();
      }

      const url = new URL(window.location.href);
      const attemptAuditId = props.auditBatch.attemptAudits[index]; // these are the ids, is not populated
      const currentAttemptAuditId = url.searchParams.get('attemptAuditId');
      if (attemptAuditId && attemptAuditId !== currentAttemptAuditId) {
        url.searchParams.set('attemptAuditId', attemptAuditId);
        window.history.pushState({}, '', url.toString());
      }
    },
    [
      props.currentAttemptAudit,
      props.setSelectedAnnotation,
      resetHasDrawerBeenOpened,
      resetPinStore,
      setSelectedAttempt,
      props.auditBatch.attemptAudits,
    ],
  );

  useEffect(() => {
    const url = new URL(window.location.href);
    const attemptAuditId = url.searchParams.get('attemptAuditId');
    if (attemptAuditId) {
      const index = props.auditBatch.attemptAudits.findIndex(
        attemptAudit => attemptAudit === attemptAuditId,
      );
      if (index !== -1) {
        updateAttemptAudit(index);
      }
    }
  }, [updateAttemptAudit, props.auditBatch.attemptAudits]);

  const closeOperationModal = useCallback(() => setIsOperationModalOpen(false), []);

  const { isInReview, qaOperation } = useQAOperationContext();

  return (
    <>
      {qaOperation && (
        <QAOperationHeader
          isOpen={isOperationModalOpen}
          overrideSubmitButton={!isInReview()}
          onClose={closeOperationModal}
        />
      )}
      <div className="flex flex-row grow" style={{ justifyContent: 'flex-start !important' }}>
        <ChatBulkAuditMultiTurnResponse
          auditBatch={props.auditBatch}
          selectedAttempt={selectedAttempt}
          setSelectedAttempt={setSelectedAttempt}
          currentAttemptAudit={props.currentAttemptAudit}
          subtaskAssignment={props.subtaskAssignment}
          setSelectedAnnotation={updateAttemptAudit}
          setShowFeedbackBar={props.setShowFeedbackBar}
          showFeedbackBar={props.showFeedbackBar}
          allowEdits={props.allowEdits}
          formattedResponseView={props.formattedResponseView}
        />
        {props.showFeedbackBar && (
          <div
            style={{
              flex: '1 0 ' + feedbackBarWidthPixels,
              overflowY: 'hidden',
              borderLeft: '1px solid rgb(229 231 235)',
            }}
          >
            <Tabs
              tabs={tabs}
              onChange={newTab => {
                if (newTab === TaskFeedbackTabLabel.ContributorFeedback && taskBarRef.current) {
                  taskBarRef.current?.autosaveFeedback().finally(() => {
                    setFeedbackType(newTab as TaskFeedbackTabLabel);
                  });
                } else if (
                  newTab === TaskFeedbackTabLabel.TaskFeedback &&
                  contributorBarRef.current
                ) {
                  contributorBarRef.current?.autosaveFeedback().finally(() => {
                    setFeedbackType(newTab as TaskFeedbackTabLabel);
                  });
                } else {
                  setFeedbackType(newTab as TaskFeedbackTabLabel);
                }
              }}
              value={feedbackType}
              className="[&_button:first-child]:px-4"
            />
            {feedbackType === TaskFeedbackTabLabel.ContributorFeedback && (
              <ChatBulkAuditContributorFeedbackBar
                auditBatch={props.auditBatch}
                currentAttemptAudit={props.currentAttemptAudit}
                setCurrentAttemptAudit={updateAttemptAudit}
                subtaskAssignment={props.subtaskAssignment}
                subtaskAssignmentsLocalCopy={props.subtaskAssignmentsLocalCopy}
                ref={contributorBarRef}
                user={props.user}
                allowEdits={props.allowEdits}
                auditorHasClaim={props.auditorHasClaim}
              />
            )}
            {feedbackType === TaskFeedbackTabLabel.TaskFeedback && (
              <ChatBulkAuditTaskFeedbackBar
                rubric={props.rubric}
                auditBatch={props.auditBatch}
                currentAttemptAudit={props.currentAttemptAudit}
                setCurrentAttemptAudit={updateAttemptAudit}
                subtaskAssignment={props.subtaskAssignment}
                subtaskAssignmentsLocalCopy={props.subtaskAssignmentsLocalCopy}
                ref={taskBarRef}
                user={props.user}
                userHasProjectRole={props.userHasProjectRole}
                userHasClaimTaskPerms={props.userHasClaimTaskPerms}
                allowEdits={props.allowEdits}
                auditorHasClaim={props.auditorHasClaim}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default React.memo(ChatBulkAuditPage);
